import { useState } from 'react';
import {
  AlertCircle,
  CheckCircle2,
  Download,
  FileSpreadsheet,
  Loader2,
  Upload,
  X,
} from 'lucide-react';
import { api, extractErrorMessage } from '../../lib/api';
import { ModalPortal } from '../ModalPortal';

/**
 * Modal d'import Excel des personnels (US-PERS-04).
 *
 *  - Téléchargement du modèle Excel vierge
 *  - Upload d'un fichier .xlsx
 *  - Choix du mode (create_only / update_only / upsert)
 *  - Option "envoyer email d'invitation" aux nouveaux comptes
 *  - Rapport détaillé après import (création / màj / erreurs ligne par ligne)
 */

type Mode = 'create_only' | 'update_only' | 'upsert';

interface ImportReport {
  job_id: string;
  total_rows: number;
  created_rows: number;
  updated_rows: number;
  skipped_rows: number;
  failed_rows: number;
  errors: Array<{ row: number; email: string; message: string }>;
}

export function ImportPersonnelsModal({
  onClose,
  onCompleted,
}: {
  onClose(): void;
  onCompleted(): void;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [mode, setMode] = useState<Mode>('upsert');
  const [sendInvitation, setSendInvitation] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [report, setReport] = useState<ImportReport | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  async function downloadTemplate() {
    try {
      const res = await api.get('/personnels/import-template.xlsx', {
        responseType: 'blob',
      });
      const blob = new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'personnels-template.xlsx';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setErrorMessage(extractErrorMessage(err));
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!file) {
      setErrorMessage('Sélectionnez un fichier .xlsx');
      return;
    }
    setUploading(true);
    setErrorMessage(null);
    setReport(null);
    try {
      const form = new FormData();
      form.append('file', file);
      const params = new URLSearchParams();
      params.set('mode', mode);
      params.set('send_invitation', String(sendInvitation));
      const res = await api.post<{ data: ImportReport }>(`/personnels/import?${params}`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setReport(res.data.data);
    } catch (err) {
      setErrorMessage(extractErrorMessage(err));
    } finally {
      setUploading(false);
    }
  }

  return (
    <ModalPortal>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl max-w-2xl w-full shadow-2xl max-h-[90vh] flex flex-col">
          <div className="flex items-center justify-between p-4 border-b border-gray-200 flex-shrink-0">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center">
              <FileSpreadsheet className="w-5 h-5 mr-2 text-primary-600" />
              Importer des personnels depuis Excel
            </h2>
            <button
              onClick={onClose}
              className="p-1 rounded-lg hover:bg-gray-100"
              aria-label="Fermer"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {errorMessage && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-3 flex items-start space-x-2">
                <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-700">{errorMessage}</p>
              </div>
            )}

            {!report ? (
              <form id="import-form" onSubmit={handleSubmit} className="space-y-4">
                {/* Template */}
                <div className="rounded-lg bg-blue-50 border border-blue-200 p-3 text-sm text-blue-900">
                  <p className="font-medium mb-1">Format attendu</p>
                  <p className="text-xs mb-2">
                    Colonnes obligatoires : <code className="font-mono">email</code>,{' '}
                    <code className="font-mono">first_name</code>,{' '}
                    <code className="font-mono">last_name</code>. Optionnelles :{' '}
                    <code className="font-mono">role</code> (nom du rôle),{' '}
                    <code className="font-mono">is_active</code>. Toute colonne supplémentaire est
                    considérée comme un champ personnalisé (Form Builder).
                  </p>
                  <button
                    type="button"
                    onClick={downloadTemplate}
                    className="inline-flex items-center text-xs font-medium text-primary-600 hover:text-primary-700"
                  >
                    <Download className="w-3.5 h-3.5 mr-1" />
                    Télécharger le modèle Excel
                  </button>
                </div>

                {/* File picker */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Fichier Excel
                  </label>
                  <input
                    type="file"
                    accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                    onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                    className="block w-full text-sm text-gray-700 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100"
                  />
                  {file && (
                    <p className="text-xs text-gray-500 mt-1">
                      {file.name} · {(file.size / 1024).toFixed(1)} ko
                    </p>
                  )}
                </div>

                {/* Mode */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Mode d'import
                  </label>
                  <div className="space-y-2">
                    <Radio
                      name="mode"
                      value="upsert"
                      checked={mode === 'upsert'}
                      onChange={() => setMode('upsert')}
                      label="Upsert"
                      hint="Crée les utilisateurs absents, met à jour les existants (recommandé)."
                    />
                    <Radio
                      name="mode"
                      value="create_only"
                      checked={mode === 'create_only'}
                      onChange={() => setMode('create_only')}
                      label="Création seule"
                      hint="Ignore les emails déjà présents."
                    />
                    <Radio
                      name="mode"
                      value="update_only"
                      checked={mode === 'update_only'}
                      onChange={() => setMode('update_only')}
                      label="Mise à jour seule"
                      hint="Met à jour les emails existants, ignore les nouveaux."
                    />
                  </div>
                </div>

                {/* Send invitation */}
                <label className="flex items-start cursor-pointer">
                  <input
                    type="checkbox"
                    checked={sendInvitation}
                    onChange={(e) => setSendInvitation(e.target.checked)}
                    className="w-4 h-4 mt-0.5 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <div className="ml-2">
                    <span className="text-sm text-gray-700">
                      Envoyer un email d'invitation aux nouveaux utilisateurs
                    </span>
                    <p className="text-xs text-gray-500">
                      Le mot de passe temporaire généré sera communiqué par email. Si désactivé, le
                      mdp ne sera transmis nulle part — l'admin devra le récupérer côté serveur.
                    </p>
                  </div>
                </label>
              </form>
            ) : (
              <ImportReportView report={report} />
            )}
          </div>

          <div className="flex items-center justify-end space-x-2 p-4 border-t border-gray-200 flex-shrink-0">
            {report ? (
              <button
                onClick={onCompleted}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-primary-500 hover:bg-primary-600"
              >
                Fermer et rafraîchir
              </button>
            ) : (
              <>
                <button
                  onClick={onClose}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  form="import-form"
                  disabled={uploading || !file}
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-primary-500 hover:bg-primary-600 disabled:opacity-50"
                >
                  {uploading ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Upload className="w-4 h-4 mr-2" />
                  )}
                  Lancer l'import
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </ModalPortal>
  );
}

function Radio({
  name,
  value,
  checked,
  onChange,
  label,
  hint,
}: {
  name: string;
  value: string;
  checked: boolean;
  onChange(): void;
  label: string;
  hint: string;
}) {
  return (
    <label className="flex items-start cursor-pointer">
      <input
        type="radio"
        name={name}
        value={value}
        checked={checked}
        onChange={onChange}
        className="w-4 h-4 mt-0.5 border-gray-300 text-primary-600 focus:ring-primary-500"
      />
      <div className="ml-2">
        <span className="text-sm font-medium text-gray-900">{label}</span>
        <p className="text-xs text-gray-500">{hint}</p>
      </div>
    </label>
  );
}

function ImportReportView({ report }: { report: ImportReport }) {
  const ok = report.failed_rows === 0;
  return (
    <div className="space-y-4">
      <div
        className={`rounded-lg border p-3 flex items-start space-x-2 ${
          ok
            ? 'border-green-200 bg-green-50 text-green-800'
            : 'border-amber-200 bg-amber-50 text-amber-800'
        }`}
      >
        {ok ? (
          <CheckCircle2 className="w-5 h-5 flex-shrink-0 mt-0.5" />
        ) : (
          <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
        )}
        <div className="text-sm">
          <p className="font-medium">
            {ok ? 'Import terminé avec succès' : 'Import terminé avec des erreurs'}
          </p>
          <p className="text-xs mt-1">{report.total_rows} ligne(s) traitée(s).</p>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <Stat label="Créés" value={report.created_rows} tone="green" />
        <Stat label="Mis à jour" value={report.updated_rows} tone="blue" />
        <Stat label="Ignorés" value={report.skipped_rows} tone="gray" />
        <Stat label="Erreurs" value={report.failed_rows} tone="red" />
      </div>

      {report.errors.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-gray-900 mb-2">Détails des erreurs</h3>
          <div className="rounded-lg border border-gray-200 max-h-64 overflow-y-auto">
            <table className="min-w-full divide-y divide-gray-200 text-xs">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th className="px-3 py-2 text-left font-medium text-gray-500 uppercase">Ligne</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-500 uppercase">Email</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-500 uppercase">
                    Message
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {report.errors.map((e, i) => (
                  <tr key={i}>
                    <td className="px-3 py-2 text-gray-700">{e.row}</td>
                    <td className="px-3 py-2 text-gray-700 font-mono">{e.email}</td>
                    <td className="px-3 py-2 text-red-700">{e.message}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone: 'green' | 'blue' | 'gray' | 'red';
}) {
  const tones: Record<typeof tone, string> = {
    green: 'bg-green-50 text-green-700',
    blue: 'bg-blue-50 text-blue-700',
    gray: 'bg-gray-100 text-gray-700',
    red: 'bg-red-50 text-red-700',
  };
  return (
    <div className={`rounded-lg p-3 ${tones[tone]}`}>
      <p className="text-xs uppercase tracking-wide opacity-70">{label}</p>
      <p className="text-xl font-bold mt-1">{value}</p>
    </div>
  );
}
